import { useState, useEffect, useCallback, useRef } from 'react';
import { getGatewayStatus } from '../api/apiService';
import { GATEWAY_STATUS, GATEWAY_STATUS_POLL_MS } from '../constants/gateway';
import { computeGatewayStatus } from '../utils/gatewayStatus';

const MOCK_STATUS = process.env.REACT_APP_MOCK_GATEWAY_STATUS?.toUpperCase();

const buildMockStatus = (projectId) => ({
  projectId,
  gatewayName: `${projectId}_GATEWAY_IOTC`,
  status: MOCK_STATUS === GATEWAY_STATUS.OFFLINE ? GATEWAY_STATUS.OFFLINE : GATEWAY_STATUS.ONLINE,
  lastHeartbeat: new Date().toISOString(),
  secondsSinceHeartbeat: MOCK_STATUS === GATEWAY_STATUS.OFFLINE ? 300 : 12,
  firmware: 'RUT2M_R_00.07.16.3 (mock)',
  available: true,
});

/**
 * Polls gateway heartbeat status for a single site.
 * Fails silently when the API is not deployed yet (banner stays hidden).
 */
const useGatewayStatus = (projectId) => {
  const [state, setState] = useState({
    status: GATEWAY_STATUS.UNKNOWN,
    gatewayName: null,
    lastHeartbeat: null,
    secondsSinceHeartbeat: null,
    firmware: null,
    loading: true,
    available: false,
  });
  const lastHeartbeatRef = useRef(null);

  const applyRecord = useCallback((record) => {
    if (!record) {
      setState((prev) => ({
        ...prev,
        loading: false,
        available: false,
      }));
      return;
    }

    lastHeartbeatRef.current = record.lastHeartbeat;
    const computed =
      record.status && record.status !== GATEWAY_STATUS.UNKNOWN
        ? {
            status: record.status,
            secondsSinceHeartbeat: record.secondsSinceHeartbeat ?? null,
          }
        : computeGatewayStatus(record.lastHeartbeat);

    setState({
      status: computed.status,
      gatewayName: record.gatewayName ?? null,
      lastHeartbeat: record.lastHeartbeat ?? null,
      secondsSinceHeartbeat: computed.secondsSinceHeartbeat,
      firmware: record.firmware ?? null,
      loading: false,
      available: true,
    });
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!projectId) {
      setState((prev) => ({ ...prev, loading: false, available: false }));
      return;
    }

    if (MOCK_STATUS && MOCK_STATUS !== GATEWAY_STATUS.UNKNOWN) {
      applyRecord(buildMockStatus(projectId));
      return;
    }

    try {
      const record = await getGatewayStatus(projectId);
      applyRecord(record);
    } catch {
      setState((prev) => ({ ...prev, loading: false, available: false }));
    }
  }, [projectId, applyRecord]);

  useEffect(() => {
    lastHeartbeatRef.current = null;
    setState({
      status: GATEWAY_STATUS.UNKNOWN,
      gatewayName: null,
      lastHeartbeat: null,
      secondsSinceHeartbeat: null,
      firmware: null,
      loading: true,
      available: false,
    });
    fetchStatus();
    const pollId = setInterval(fetchStatus, GATEWAY_STATUS_POLL_MS);
    return () => clearInterval(pollId);
  }, [projectId, fetchStatus]);

  // Refresh "sec ago" between API polls using router heartbeat time
  useEffect(() => {
    if (!lastHeartbeatRef.current || !state.available) return undefined;

    const tick = () => {
      const { status, secondsSinceHeartbeat } = computeGatewayStatus(lastHeartbeatRef.current);
      setState((prev) =>
        prev.available
          ? { ...prev, status, secondsSinceHeartbeat }
          : prev
      );
    };

    tick();
    const tickId = setInterval(tick, 1000);
    return () => clearInterval(tickId);
  }, [state.available, state.lastHeartbeat]);

  return state;
};

export default useGatewayStatus;
